import {
    DSAR_DETAILS_FIELD_IDS,
    DSAR_DISPLAY_OPTIONS,
    DSAR_LEGACY_FIELD_IDS,
    DSAR_REQUEST_TYPE_AGENT_VERIFICATION_LEVELS,
    DSAR_REQUEST_TYPE_VERIFICATION_LEVELS,
    DSAR_STANDARD_FIELDS_I18N,
} from '/b2b/dsarForms/constants';

import { DSAR_EMAIL_URL } from '#config/runtimeconfig';
import castArray from 'lodash/castArray';
import { formatOrganizations } from '../organizations/format';
import { htmlDecode } from '../common/helpers/String';
import identity from 'lodash/identity';
import isNil from 'lodash/isNil';
import { makeResultsFormatter } from '../common/services/helpers';
import omitBy from 'lodash/omitBy';
import { v4 as uuid } from 'uuid';
import { validateEmail } from '../common/helpers/Email';

const {
    IDENTIFICATION_FILE,
    REPRESENTATIVE_AUTHORIZATION_FILE,
    REPRESENTATIVE_TYPE,
    REQUEST_TYPE,
} = DSAR_DETAILS_FIELD_IDS;
const { EMAIL_AND_IDENTITY } = DSAR_REQUEST_TYPE_VERIFICATION_LEVELS;
const { NONE } = DSAR_REQUEST_TYPE_AGENT_VERIFICATION_LEVELS;

/**
 * Formats special fields that are managed via the request types tab instead of form fields tab
 *
 * @param {Object} field - The field object to format
 * @param {Array} requestTypes - Array of request type objects
 * @param {Function} enabledFilter - Function to determine if a field is enabled for a given request type
 * @returns {Object} The formatted field object
 */
export const formatRequestTypeManagedField = (field = {}, requestTypes = [], enabledFilter) => {
    const enabledAllRequestTypes = requestTypes.every(enabledFilter);
    const enabledSomeRequestTypes = requestTypes.some(enabledFilter) && !enabledAllRequestTypes;
    const enabledNoRequestTypes = !requestTypes.some(enabledFilter);

    if (enabledAllRequestTypes) {
        field.excluded = false;
        field.displayOption = DSAR_DISPLAY_OPTIONS.DEFAULT;
        field.displayOptionRequestTypes = [];
    } else if (enabledSomeRequestTypes) {
        field.excluded = false;
        field.displayOption = DSAR_DISPLAY_OPTIONS.ENABLED;
        const enabledRequestTypes = requestTypes
            .filter(enabledFilter)
            .map(({ requestTypeId: value, name: label } = {}) => ({
                disabled: true,
                label,
                value,
            }));
        field.displayOptionRequestTypes = enabledRequestTypes;
    } else if (enabledNoRequestTypes) {
        field.excluded = true;
        field.displayOption = DSAR_DISPLAY_OPTIONS.DISABLED;
        field.displayOptionRequestTypes = [];
    }

    return field;
};

/**
 * Formats the identity verification field
 *
 * @param {Object} field - The field object to format
 * @param {Array} requestTypes - Array of request type objects
 * @returns {Object} The formatted field object
 */
export const formatIdVerificationField = (field = {}, requestTypes = []) => {
    const idVerificationEnabled = ({
        verification_level,
        verificationLevel = verification_level,
    }) => verificationLevel === EMAIL_AND_IDENTITY;

    return formatRequestTypeManagedField(field, requestTypes, idVerificationEnabled);
};

/**
 * Formats the representative authorization field
 *
 * @param {Object} field - The field object to format
 * @param {Array} requestTypes - Array of request type objects
 * @returns {Object} The formatted field object
 */
export const formatRepresentativeAuthorizationField = (field = {}, requestTypes = []) => {
    const authorizationEnabled = ({
        agent_verification_level,
        agentVerificationLevel = agent_verification_level,
    }) => agentVerificationLevel !== NONE;

    return formatRequestTypeManagedField(field, requestTypes, authorizationEnabled);
};

/**
 * Formats the representative type field
 *
 * @param {Object} field - The field object to format
 * @param {Array} requestTypes - Array of request type objects
 * @returns {Object} The formatted field object
 */
export const formatRepresentativeTypeField = (field = {}, requestTypes = []) => {
    const representativeTypeEnabled = ({
        agent_submission_allowed,
        agentSubmissionAllowed = agent_submission_allowed,
    }) => agentSubmissionAllowed;

    return formatRequestTypeManagedField(field, requestTypes, representativeTypeEnabled);
};

/**
 * Formats standard fields
 *
 * @param {Object} field - The field object to format
 * @param {Array} requestTypes - Array of request type objects
 * @returns {Object} The formatted field object
 */
const formatStandardField = (field = {}, requestTypes = []) => {
    for (const [key, entry] of Object.entries(field)) {
        if (key === 'excluded' && entry !== undefined) {
            field.displayOption =
                entry === true ? DSAR_DISPLAY_OPTIONS.DISABLED : DSAR_DISPLAY_OPTIONS.DEFAULT;
        }

        if (key === 'conditional' && entry?.id === REQUEST_TYPE && entry?.value) {
            const displayOptionRequestTypes = castArray(entry.value)
                .map(val => {
                    const requestType = requestTypes.find(
                        ({ requestTypeId }) => requestTypeId === val
                    );
                    return requestType
                        ? {
                              label: requestType.name,
                              value: val,
                          }
                        : null;
                })
                .filter(Boolean);
            field.displayOption = displayOptionRequestTypes.length
                ? DSAR_DISPLAY_OPTIONS.ENABLED
                : DSAR_DISPLAY_OPTIONS.DEFAULT;
            field.displayOptionRequestTypes = displayOptionRequestTypes;
        }
    }

    if (!field.displayOption) {
        field.displayOption = DSAR_DISPLAY_OPTIONS.DEFAULT;
    }

    return field;
};

/**
 * Formats a DSAR form field
 *
 * @param {Object} fieldToFormat - The field object to format
 * @param {Array} requestTypes - Array of request type objects
 * @returns {Object} The formatted field object
 */
const formatField = (fieldToFormat = {}, requestTypes = []) => {
    const field = omitBy(fieldToFormat, isNil);

    switch (field.id) {
        case IDENTIFICATION_FILE:
            return formatIdVerificationField(field, requestTypes);
        case REPRESENTATIVE_AUTHORIZATION_FILE:
            return formatRepresentativeAuthorizationField(field, requestTypes);
        case REPRESENTATIVE_TYPE:
            return formatRepresentativeTypeField(field, requestTypes);
        default:
            return formatStandardField(field, requestTypes);
    }
};

/**
 * Creates a map of fields
 *
 * @param {Array} customFields - Array of custom field objects
 * @param {Array} requestTypes - Array of request type objects
 * @returns {Object} The map of fields
 */
const createFieldsMap = (customFields = [], requestTypes = []) => {
    // start with default fields
    const defaultFieldsMap = Object.entries(DSAR_STANDARD_FIELDS_I18N).reduce((acc, [key, val]) => {
        acc[key] = { ...val, id: key, name: key };
        return acc;
    }, {});

    // if no custom fields have been saved yet, return default fields
    if (!customFields?.length) {
        return Object.entries(defaultFieldsMap).reduce((acc, [id, field], index) => {
            const formattedField = formatField(field, requestTypes);
            acc[id] = { ...formattedField, ordinal: index * 100 };
            return acc;
        }, {});
    }

    // filter out legacy field IDs from custom fields
    // TODO: can remove this filter once we've scrubbed legacy fields from the database
    const validCustomFields = customFields.filter(({ id }) => !DSAR_LEGACY_FIELD_IDS.includes(id));

    const fieldsMap = {};
    let orderIndex = 0;

    // process remaining valid custom fields (these take precedence and maintain their order)
    for (const customField of validCustomFields) {
        const { id, name = id } = customField;

        orderIndex++;

        if (defaultFieldsMap[id]) {
            // merge custom field with default field (custom settings override default)
            const mergedField = {
                ...defaultFieldsMap[id],
                ...customField,
                id,
                name: id,
            };

            const field = formatField(mergedField, requestTypes);

            fieldsMap[id] = { ...field, ordinal: orderIndex * 100 };

            // remove from defaults map so we don't process it again
            delete defaultFieldsMap[id];
        } else {
            // custom field that doesn't exist in defaults (user created field)
            const field = formatField(customField, requestTypes);
            const fieldId = id || uuid('customFormField');
            const fieldName = name.indexOf('o-') === 0 ? name : `o-${name}`;

            fieldsMap[fieldId] = {
                ...field,
                id: fieldId,
                name: fieldName,
                ordinal: orderIndex * 100,
            };
        }
    }

    // add any remaining default fields that weren't overridden by custom fields is a new default
    // these get higher ordinal values to appear after existing custom fields
    // they should be excluded by default as we don't want to enable new fields on existing forms
    for (const [id, defaultField] of Object.entries(defaultFieldsMap)) {
        const field = formatField(
            {
                ...defaultField,
                excluded: true,
            },
            requestTypes
        );

        fieldsMap[id] = {
            ...field,
            ordinal: orderIndex * 100,
        };
    }

    return fieldsMap;
};

export const formatDSARForm = data => {
    const {
        active,
        allowListEmails = [],
        customFields = [],
        customerId,
        dataStoreCount = 0,
        description,
        escalationEmails = [],
        form_id,
        formId = form_id,
        fromName,
        fromCompany,
        generatedInbox,
        aiEmailParsing = false,
        name,
        requestTypes,
        customRequestTypes: providedCustomRequestTypes,
        styling,
        orgIds = [],
        organizations = orgIds,
        duplicateReject,
        unverifiedEmailReject,
        captchaEnabled,
        internalDueDays = null,
        openActionItemsCount = 0,
        openRequestsCount = 0,
        templates = [],
        templatesCount,
        trend = [],
        ssoConfigId,
        ssoRequired,
        ssoConfigName,
        workflowId,
    } = data;

    const fields = createFieldsMap(customFields, requestTypes);
    const newCustomFields = Object.values(fields).sort((a, b) => a.ordinal - b.ordinal);

    const persistedFormFieldsCount = newCustomFields.filter(
        ({ conditional } = {}) => !(conditional && conditional.id !== 'request-type')
    ).length;

    // The update PATCH response (and its fallback body) carries customRequestTypes rather than
    // requestTypes, so fall back to it to avoid zeroing the request-types count on save.
    const sourceRequestTypes = requestTypes ?? providedCustomRequestTypes ?? [];

    const customRequestTypes = sourceRequestTypes.map(requestType => ({
        ...requestType,
        id: requestType.requestTypeId ?? requestType.id,
    }));

    const formattedRequestTypes = customRequestTypes.reduce((types, type) => {
        const { id } = type;
        types[id] = { ...type };
        return types;
    }, {});

    return {
        active,
        allowListEmails,
        customer: customerId,
        customFields: newCustomFields,
        persistedFormFieldsCount,
        customRequestTypes,
        dataStoreCount,
        description,
        escalationEmails,
        duplicateReject,
        unverifiedEmailReject,
        captchaEnabled,
        internalDueItems: !internalDueDays ? 'internalDueDefault' : 'internalDueDays',
        internalDueDays,
        organizations: formatOrganizations(organizations),
        fields,
        formId,
        fromName,
        fromCompany: htmlDecode(fromCompany),
        generatedInbox: validateEmail(generatedInbox)
            ? generatedInbox
            : `${customerId}_${formId}@${DSAR_EMAIL_URL}`,
        aiEmailParsing,
        name: htmlDecode(name),
        openActionItemsCount,
        openRequestsCount,
        requestTypes: formattedRequestTypes,
        // Preserve the full persisted styling object (palette, stylesheetURL,
        // showHeader, ...) while defaulting instructions to null. Collapsing this
        // to `{ instructions }` dropped palette/stylesheetURL on load, so the next
        // save PATCHed them away — wiping every form's custom styling (TRIAGE-1510).
        styling: { ...styling, instructions: styling?.instructions ?? null },
        templates,
        templatesCount,
        trend,
        consumerSsoId: ssoConfigId
            ? {
                  value: ssoConfigId,
                  label: ssoConfigName || ssoConfigId,
              }
            : null,
        // If sso_config_id is set in database, use sso_required value from database
        // Otherwise, default to true (checked)
        consumerSsoRequired:
            ssoConfigId !== null && ssoConfigId !== undefined ? ssoRequired || false : true,
        consumerSsoEnabled: !!ssoConfigId,
        workflowId,
    };
};
export const formatDSARFormResults = makeResultsFormatter(formatDSARForm);

export const formatDSARFormRequestTypeResults = makeResultsFormatter(identity);

export const formatDSARProcessingResults = makeResultsFormatter(identity);

const formatOrganizationsColumn = organizations => {
    if (organizations.length === 0) {
        return '—';
    }
    if (organizations.length === 1 && organizations[0].name === 'ALL') {
        return '—';
    }
    return organizations.map(org => org.name).join(', ');
};

export const formatCSVRow = row => {
    return {
        name: row.name,
        description: row.description,
        organizations: formatOrganizationsColumn(row.organizations),
    };
};
